import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import AOS from 'aos';
import Modal from '../components/Modal';
import RecentAttempts from '../components/RecentAttempts';
import { reviewerAPI, examAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { canAccessReviewer } from '../utils/subscription';
import { ExamNotesLightningIcon, MetricTimeIcon, MetricItemsIcon, MetricPassingScoreIcon, MetricStatusIcon } from '../components/Icons';
import { TextWithNewlines } from '../utils/text';
import ExamDetailsSkeleton from '../components/skeletons/ExamDetailsSkeleton';
import { BANNER_IMAGE_MAP } from '../data/reviewers';
import { trackExamSelected } from '../services/analytics';

/* ── Exam Breakdown helpers ──────────────────────────────── */

const SECTION_CONFIG = {
  verbal: { label: 'Verbal', color: '#14B8A6' },
  analytical: { label: 'Analytical', color: '#3B82F6' },
  clerical: { label: 'Clerical', color: '#3B82F6' },
  numerical: { label: 'Numerical', color: '#F59E0B' },
  general_info: { label: 'General Info', color: '#EC4899' },
  'general info': { label: 'General Info', color: '#EC4899' },
};
const FALLBACK_COLORS = ['#8B5CF6', '#06B6D4', '#10B981', '#F43F5E', '#6366F1', '#EF4444'];

function getSectionConfig(sectionKey, index) {
  const key = (sectionKey || '').toLowerCase().trim();
  return SECTION_CONFIG[key] || {
    label: sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1),
    color: FALLBACK_COLORS[index % FALLBACK_COLORS.length],
  };
}

/** Pure-SVG donut chart with centered total-items label. */
function DonutChart({ segments, total }) {
  const R = 70;
  const CX = 100;
  const CY = 100;
  const STROKE = 28;
  const C = 2 * Math.PI * R; // circumference ≈ 439.82

  let cumulative = 0;
  const arcs = segments.map((seg, i) => {
    const fraction = total > 0 ? seg.count / total : 0;
    const dashLen = fraction * C;
    // SVG circles start at 3 o'clock; rotate -90deg to start at 12 o'clock
    const offset = -(cumulative / (total || 1)) * C;
    cumulative += seg.count;
    return { ...seg, dashLen, offset };
  });

  return (
    <svg viewBox="0 0 200 200" width={182} height={182} style={{ width: '182px', height: '182px', display: 'block', margin: '0 auto' }}>
      {/* background ring */}
      <circle
        cx={CX} cy={CY} r={R}
        fill="none"
        stroke="#E5E7EB"
        strokeWidth={STROKE}
      />
      {/* colored arcs */}
      {arcs.map((arc, i) => (
        <circle
          key={i}
          cx={CX} cy={CY} r={R}
          fill="none"
          stroke={arc.color}
          strokeWidth={STROKE}
          strokeDasharray={`${arc.dashLen} ${C}`}
          strokeDashoffset={arc.offset}
          transform={`rotate(-90 ${CX} ${CY})`}
          style={{ transition: 'stroke-dasharray 0.4s ease' }}
        />
      ))}
      {/* center label */}
      <text x={CX} y={CY - 8} textAnchor="middle" className="font-inter" fontSize="11" fill="#6C737F">Total Items</text>
      <text x={CX} y={CY + 14} textAnchor="middle" className="font-inter" fontSize="26" fontWeight="700" fill="#421A83">{total}</text>
    </svg>
  );
}

/** Exam Breakdown section: donut chart + table. */
function ExamBreakdown({ sectionDistribution, totalItems, disclaimer }) {
  const total = totalItems || sectionDistribution.reduce((s, x) => s + x.count, 0);
  const segments = sectionDistribution.map((s, i) => ({
    ...getSectionConfig(s.section, i),
    count: s.count,
    percent: total > 0 ? Math.round((s.count / total) * 100) : 0,
  }));

  return (
    <section
      className="mb-8 mt-[8px] max-w-[808px]"
      data-aos="fade-up"
      data-aos-duration="500"
      data-aos-delay="120"
    >
      <h2 className="font-inter font-bold text-[24px] text-[#45464E] mb-5">
        Exam Breakdown
      </h2>
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-8">
        {/* Donut chart */}
        <div style={{ width: 262, height: 214, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DonutChart segments={segments} total={total} />
        </div>
        {/* Table */}
        <div className="flex-1 min-w-0 max-w-[448px] w-full self-center">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#E5E7EB]">
                <th className="font-inter font-normal text-[13px] text-[#6C737F] pb-2 pr-4 w-1/2">Section</th>
                <th className="font-inter font-normal text-[13px] text-[#6C737F] pb-2 pr-4 text-right">No. of Items</th>
                <th className="font-inter font-normal text-[13px] text-[#6C737F] pb-2 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {segments.map((seg, i) => (
                <tr key={i} className="border-b border-[#F3F4F6]">
                  <td className="py-[10px] pr-4">
                    <span className="flex items-center gap-2 font-inter font-normal text-[14px] text-[#45464E]">
                      <span className="w-[10px] h-[10px] rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
                      {seg.label}
                    </span>
                  </td>
                  <td className="py-[10px] pr-4 font-inter font-bold text-[14px] text-[#45464E] text-right">{seg.count}</td>
                  <td className="py-[10px] font-inter font-bold text-[14px] text-[#45464E] text-right">{seg.percent}%</td>
                </tr>
              ))}
              <tr>
                <td className="pt-[10px] pr-4 font-inter font-bold text-[14px] text-[#45464E]">Total</td>
                <td className="pt-[10px] pr-4 font-inter font-bold text-[14px] text-[#45464E] text-right">{total}</td>
                <td className="pt-[10px] font-inter font-bold text-[14px] text-[#45464E] text-right">100%</td>
              </tr>
            </tbody>
          </table>
          {disclaimer && (
            <p className="font-inter font-normal italic text-[11px] text-[#6C737F] mt-3 leading-[18px]">
              {disclaimer}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */

const ExamDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const fromLibrary = new URLSearchParams(location.search).get('from') === 'library';
  const [reviewer, setReviewer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inProgress, setInProgress] = useState(null);   // from getReviewerProgress
  const [history, setHistory] = useState([]);            // completed attempts for this reviewer
  const [confirmingNew, setConfirmingNew] = useState(false); // inline "Start New" confirm state

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    // Wait until auth has resolved so the premium guard below sees the real
    // user (avoids redirecting a paying user mid-hydration on a hard refresh).
    if (authLoading) return;
    let cancelled = false;
    async function fetchData() {
      try {
        const [revRes, progRes] = await Promise.all([
          reviewerAPI.getById(id),
          examAPI.getReviewerProgress(id),
        ]);
        if (cancelled) return;
        if (revRes.success) {
          // URL guard: a free user landing directly on a premium exam (e.g. the
          // full mock) is redirected to the upgrade page instead of being able
          // to start it.
          if (!canAccessReviewer(revRes.data, { isAuthenticated, user })) {
            navigate('/dashboard/settings/update-subscription', { replace: true });
            return;
          }
          setReviewer(revRes.data);
          trackExamSelected(id, revRes.data.title);
        }
        if (progRes.success) {
          setInProgress(progRes.data.inProgress || null);
          setHistory(progRes.data.history || []);
        }
      } catch (err) {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [id, authLoading, isAuthenticated, user, navigate]);

  const exam = reviewer?.examDetails;

  /** Format seconds into digital time format (HH:MM:SS). */
  const formatRemainingTime = (seconds) => {
    if (!seconds || seconds <= 0) return '00:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
  };

  useEffect(() => {
    AOS.refresh();
  }, [reviewer]);

  if (loading) return <ExamDetailsSkeleton />;

  if (!reviewer || !exam) {
    return (
      <div className="min-h-screen bg-white">
        <main className="max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-20 py-8">
          <p className="font-inter text-[#45464E]">Exam not found.</p>
          <Link
            to={fromLibrary ? '/dashboard/library' : '/dashboard/all-reviewers'}
            className="font-inter text-[#6E43B9] hover:underline mt-4 inline-block"
          >
            Back to {fromLibrary ? 'My Library' : 'All Reviewers'}
          </Link>
        </main>
      </div>
    );
  }

  const { title } = reviewer;

  return (
    <div className="min-h-screen bg-[#F5F4FF]">
      <main className="max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-20 pt-[24px] pb-[40px]">
        {/* Breadcrumbs */}
        <nav className="mb-[24px]" aria-label="Breadcrumb">
          <Link
            to={fromLibrary ? '/dashboard/library' : '/dashboard/all-reviewers'}
            className="text-[#45464E] font-inter font-normal not-italic text-[14px] hover:text-[#6E43B9] transition-colors"
          >
            {fromLibrary ? 'My Library' : 'All Reviewers'}
          </Link>
          <span className="mx-2">›</span>
          <span className="text-[#6E43B9] font-inter font-normal not-italic text-[14px]">{title}</span>
        </nav>

        {/* Top banner (spans above all cards) */}
        {/*
          TEMP: Hide banner for "mock-free" reviewers until the final image is ready.
          Uncomment the block (and remove this wrapper) once the asset is added.
        */}
        {!String(reviewer?.type || '').toLowerCase().startsWith('mock-free') && (
          <div
            className="w-full h-[190px] rounded-[8px] bg-gradient-to-br from-[#6E43B9]/20 to-[#421983]/30 flex items-center justify-center mb-[16px] overflow-hidden"
            data-aos="fade-up"
            data-aos-duration="500"
          >
            {exam.bannerImage ? (
              <img
                src={BANNER_IMAGE_MAP[exam.bannerImage]}
                alt=""
                className="w-full h-full object-cover object-center"
              />
            ) : (
              <span className="font-inter text-[#6E43B9]/60 text-sm">Banner image placeholder</span>
            )}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-[24px] items-start">
          {/* Left column: title, metrics, button, coverage — first on mobile (top), left on lg */}
          <div className="order-1 lg:order-1 w-full lg:w-auto lg:flex-1 lg:min-w-0 bg-[#FFFFFF] p-[24px] rounded-[12px]">
            <h1
              className="font-inter font-semibold not-italic text-[22px] text-[#45464E] mt-6 mb-[10px]"
              data-aos="fade-up"
              data-aos-duration="500"
              data-aos-delay="50"
            >
              {title}
            </h1>

            {/* Description (introShort + introFull) — shown right after title per Figma */}
            {(exam.introShort || exam.introFull) && (
              <section
                className="mb-6"
                data-aos="fade-up"
                data-aos-duration="500"
                data-aos-delay="70"
              >
                {exam.introShort && (
                  <TextWithNewlines as="p" className="font-inter font-bold text-[15px] text-[#45464E] mb-2">{exam.introShort}</TextWithNewlines>
                )}
                {exam.introFull && (
                  <TextWithNewlines as="p" className="font-inter font-normal text-[15px] text-[#45464E] leading-[26px]">{exam.introFull}</TextWithNewlines>
                )}
              </section>
            )}

            {/* Metrics + Buttons */}
            <div
              className="mb-[32px]"
              data-aos="fade-up"
              data-aos-duration="500"
              data-aos-delay="100"
            >
              {/* 4 inline metrics */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-4 sm:flex sm:flex-wrap sm:gap-x-28 sm:gap-y-3">
                {/* Time */}
                <div className="flex items-center gap-2">
                  <MetricTimeIcon className="w-[22px] h-[22px] shrink-0" />
                  <div className="flex flex-col">
                    <span className="font-inter font-medium text-[12px] text-[#45464E] leading-tight">
                      {inProgress ? 'Remaining Time' : 'Time'}
                    </span>
                    <strong className="font-inter font-medium text-[16px] text-[#421A83] leading-tight">
                      {inProgress?.remainingSeconds != null
                        ? formatRemainingTime(inProgress.remainingSeconds)
                        : exam.timeFormatted}
                    </strong>
                  </div>
                </div>
                {/* Total Items */}
                <div className="flex items-center gap-2">
                  <MetricItemsIcon className="w-[22px] h-[22px] shrink-0" />
                  <div className="flex flex-col">
                    <span className="font-inter font-medium text-[12px] text-[#45464E] leading-tight">Total Items</span>
                    <strong className="font-inter font-medium text-[16px] text-[#421A83] leading-tight">
                      {exam.itemsCount}
                    </strong>
                  </div>
                </div>
                {/* Passing Score */}
                <div className="flex items-center gap-2">
                  <MetricPassingScoreIcon className="w-[22px] h-[22px] shrink-0" />
                  <div className="flex flex-col">
                    <span className="font-inter font-medium text-[12px] text-[#45464E] leading-tight">Passing Score</span>
                    <strong className="font-inter font-medium text-[16px] text-[#421A83] leading-tight">
                      {reviewer?.examConfig?.passingThreshold != null
                        ? `${reviewer.examConfig.passingThreshold}%`
                        : 'N/A'}
                    </strong>
                  </div>
                </div>
                {/* Status */}
                <div className="flex items-center gap-2">
                  <MetricStatusIcon className="w-[22px] h-[22px] shrink-0" />
                  <div className="flex flex-col">
                    <span className="font-inter font-medium text-[12px] text-[#45464E] leading-tight">Status</span>
                    <strong className="font-inter font-medium text-[16px] text-[#421A83] leading-tight">
                      {inProgress
                        ? 'In Progress'
                        : history.length > 0
                          ? (history[0].result?.passed ? 'PASSED! 🎉' : 'FAILED')
                          : exam.progress}
                    </strong>
                  </div>
                </div>
              </div>

              {/* ── Buttons ── */}
              {inProgress ? (
                /* Case B — in-progress attempt exists */
                <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/exam/${id}/start${fromLibrary ? '?from=library' : ''}`)}
                    className="w-full sm:w-auto h-[48px] font-inter font-regular text-[16px] text-[#421A83] py-[11px] px-10 rounded-[4px] bg-[#FFC92A] hover:opacity-95 transition-opacity"
                  >
                    Continue Exam
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingNew(true)}
                    className="w-full sm:w-auto h-[48px] font-inter font-regular text-[16px] text-[#737373] py-[11px] px-10 rounded-[4px] border border-[#737373] bg-white hover:bg-gray-50 transition-colors"
                  >
                    Start New Exam
                  </button>
                  {history.length > 0 && (
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/results/${history[0]._id}${fromLibrary ? '?from=library' : ''}`)}
                      className="w-full sm:w-auto h-[48px] font-inter font-regular text-[16px] text-[#737373] py-[11px] px-10 rounded-[4px] border border-[#737373] bg-white hover:bg-gray-50 transition-colors"
                    >
                      View Latest Result
                    </button>
                  )}
                </div>
              ) : (
                /* Case A — no active attempt */
                <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/exam/${id}/start${fromLibrary ? '?from=library' : ''}`)}
                    className="w-full sm:w-auto h-[48px] font-inter font-regular text-[16px] text-[#421A83] py-[11px] px-10 rounded-[4px] bg-[#FFC92A] hover:opacity-95 transition-opacity"
                  >
                    Take Exam
                  </button>
                  {history.length > 0 && (
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/results/${history[0]._id}${fromLibrary ? '?from=library' : ''}`)}
                      className="w-full sm:w-auto h-[48px] font-inter font-regular text-[16px] text-[#737373] py-[11px] px-10 rounded-[4px] border border-[#737373] bg-white hover:bg-gray-50 transition-colors"
                    >
                      View Latest Result
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Attempt History */}
            {history.length > 0 && (
              <div className="mt-[24px]">
                <RecentAttempts
                  attempts={history}
                  loading={false}
                  reviewerTitle={reviewer?.title}
                  noPadding
                  largeHeading
                />
              </div>
            )}

            {/* Exam Breakdown */}
            {!(String(reviewer?.type || '').toLowerCase().includes('practice')) && reviewer?.examConfig?.sectionDistribution?.length > 0 && (
              <ExamBreakdown
                sectionDistribution={reviewer.examConfig.sectionDistribution}
                totalItems={reviewer.examConfig.totalItems || exam.itemsCount}
                disclaimer={exam.breakdownFootnote || exam.disclaimer}
              />
            )}

            {/* Applicable for */}
            {exam.applicableFor && (
              <p
                className="font-inter text-[14px] text-[#45464E] mb-2"
                data-aos="fade-up"
                data-aos-duration="500"
                data-aos-delay="100"
              >
                Applicable for: <TextWithNewlines as="strong" className="font-inter font-semibold text-[#45464E]">{exam.applicableFor}</TextWithNewlines>
              </p>
            )}

            {/* Access for */}
            {exam.accessFor && (
              <p
                className="font-inter text-[14px] text-[#45464E] mb-4"
                data-aos="fade-up"
                data-aos-duration="500"
                data-aos-delay="100"
              >
                Access: <TextWithNewlines as="strong" className="font-inter font-semibold text-[#45464E]">{exam.accessFor}</TextWithNewlines>
              </p>
            )}

            {/* Section and Topics */}
            <section
              className="pt-2"
              data-aos="fade-up"
              data-aos-duration="500"
              data-aos-delay="150"
            >
              <h2 className="font-inter font-bold text-[18px] text-[#45464E] mb-4">
                Section and Topics
              </h2>
              <ol className="list-decimal list-inside space-y-4 font-inter font-normal text-[16px] text-[#45464E]">
                {(exam.coverage || []).map((item, idx) => {
                  return (
                    <li key={idx}>
                      <span>
                        <TextWithNewlines>{item.subject}</TextWithNewlines>
                        {item.itemCount ? ` (${item.itemCount})` : ''}
                      </span>
                      <ul className="list-none font-normal text-[16px] pl-6">
                        {(item.topics || []).map((topic, i) => (
                          <li key={i} className="relative pl-3">
                            <span className="absolute left-0 top-[10px] inline-block h-[6px] w-[6px] rounded-full bg-[#45464E]" />
                            <span className="block">
                              <TextWithNewlines>{topic}</TextWithNewlines>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ol>

              {/* Coverage end text */}
              {exam.coverageEndText && (
                <TextWithNewlines as="p" className="font-inter font-normal text-[16px] text-[#45464E] mt-4 leading-[24px]">
                  {exam.coverageEndText}
                </TextWithNewlines>
              )}

              {/* Difficulty text (after coverage) */}
              {exam.difficultyText && (
                <TextWithNewlines as="p" className="font-inter font-normal text-[16px] text-[#45464E] mt-4 leading-[24px]">
                  {exam.difficultyText}
                </TextWithNewlines>
              )}

              {/* Long disclaimer at page bottom (separate from chart footnote) */}
              {exam.disclaimer && exam.disclaimer !== exam.breakdownFootnote && (
                <TextWithNewlines as="p" className="font-inter font-normal italic text-[11px] text-[#6C737F] mt-4 leading-[18px]">
                  {exam.disclaimer}
                </TextWithNewlines>
              )}
            </section>
          </div>

          {/* Right column: Important Notes — bottom when wrapped on mobile, right on lg; stretch on lg so sticky has room */}
          <div className="order-2 lg:order-2 lg:flex-shrink-0 lg:max-w-[360px] lg:self-stretch">
            <section
              className="bg-[#ffffff] rounded-[12px] p-[24px] lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto"
              data-aos="fade-up"
              data-aos-duration="500"
              data-aos-delay="200"
            >
              <h2
                className="font-inter font-medium text-[16px] leading-[100%] tracking-[0] text-[#45464E] mb-4 flex items-center gap-[16px]"
              >
                <div className="bg-[#6E43B91A] w-[36px] h-[36px] rounded-[4px] flex items-center justify-center">
                  <ExamNotesLightningIcon className="w-[20px] h-[20px] text-[#6E43B9]" />
                </div>
                Important Notes:
              </h2>
              {(() => {
                const notes = exam.importantNotes || [];
                const listNotes = notes.slice(0, -1);
                const footerNote = notes[notes.length - 1];
                return (
                  <>
                    <ul className="list-none space-y-4 pl-0 font-inter text-sm">
                      {listNotes.map((note, idx) => (
                        <li key={idx}>
                          <span className="font-inter font-semibold text-[16px] text-[#45464E] leading-[20px]">{note.title}:{" "}</span>
                          <span className="font-inter font-normal text-[16px] text-[#45464E] leading-[20px]">{note.text}</span>
                        </li>
                      ))}
                    </ul>
                    {footerNote && (
                      <p className="font-inter font-normal italic text-[12px] text-[#6C737F] mt-4 leading-[18px]">
                        {footerNote.text}
                      </p>
                    )}
                  </>
                );
              })()}
            </section>
          </div>
        </div>
      </main>

      <Modal
        isOpen={confirmingNew}
        onClose={() => setConfirmingNew(false)}
        title="Start New Exam"
        titleId="start-new-exam-modal-title"
        footer={
          <>
            <button
              type="button"
              onClick={() => setConfirmingNew(false)}
              className="font-inter font-normal text-[14px] text-[#431C86] py-[11.5px] px-6 rounded-[8px] border border-[#431C86] bg-white hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => navigate(`/dashboard/exam/${id}/start?restart=true${fromLibrary ? '&from=library' : ''}`)}
              className="font-inter font-bold text-[14px] text-[#421A83] py-[11.5px] px-6 rounded-[8px] bg-[#FACC15] hover:opacity-90 transition-opacity"
            >
              Yes, Start New
            </button>
          </>
        }
      >
        <p className="font-inter font-normal text-[14px] text-[#45464E]">
          Starting a new exam will discard your current unfinished attempt.
        </p>
        <p className="font-inter font-normal text-[14px] text-[#45464E]">
          Your progress won&apos;t be saved.
        </p>
      </Modal>
    </div>
  );
};

export default ExamDetails;
